import { Box, Button, TextLink } from '@palmetto/palmetto-components';
import { useCallback, useState } from 'react';

import { ACTIVE_QUEUE_STATUSES, QueueDocumentResponse, QueueType, UserPermissions, queueStatusMap } from 'types';

import usePermissions from '../../hooks/usePermissions';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { AccountMilestoneStipulation } from '../../types/Accounts';

import WelcomeCallReviewModal from './WelcomeCallReviewModal';

const WelcomeCallQueueReview = ({
  accountId,
  recheckVerificationCall,
}: {
  accountId: string;
  recheckVerificationCall?: AccountMilestoneStipulation['recheckVerificationCall'];
}) => {
  // The queues RTK service is `@ts-nocheck`, so the hook result is untyped; annotate the row we use.
  const { data: queue }: { data?: QueueDocumentResponse } = useGetQueueByAccountIdAndTypeQuery({
    accountId,
    type: QueueType.welcomeCall,
  });
  const permissions = usePermissions();
  const isLightReachSupport = permissions.includes(UserPermissions.lightReachSupport);
  const [pendingDecision, setPendingDecision] = useState<'approve' | 'reject' | null>(null);
  const openApprove = useCallback(() => setPendingDecision('approve'), []);
  const openReject = useCallback(() => setPendingDecision('reject'), []);
  const closeModal = useCallback(() => setPendingDecision(null), []);

  if (!queue || !ACTIVE_QUEUE_STATUSES.has(queue.status)) return null;

  const { status: callStatus, disposition, dashboardUrl } = recheckVerificationCall ?? {};

  return (
    <>
      <Box
        direction="column"
        gap="xs"
        padding="sm md"
        borderWidth="xs"
        borderColor="separator"
        radius="md"
        background="primary"
        shadow="xs"
      >
        <Box direction="row" alignItems="center" gap="md">
          <Box fontWeight="medium" fontSize="sm">
            Welcome Call Review
          </Box>
          <Box fontSize="sm">{queueStatusMap[queue.status]}</Box>
          {isLightReachSupport && (
            <Box direction="row" gap="xs" style={{ marginLeft: 'auto' }}>
              <Button size="sm" variant="primary" tone="primary" onClick={openApprove}>
                Approve
              </Button>
              <Button size="sm" variant="secondary" tone="danger" onClick={openReject}>
                Fail
              </Button>
            </Box>
          )}
        </Box>
        {recheckVerificationCall && (
          <Box direction="row" gap="2xs" fontSize="xs" alignItems="center">
            <Box>Verification call:</Box>
            {callStatus && <Box fontWeight="bold">{callStatus}</Box>}
            {disposition && <Box fontWeight="bold">{`(${disposition})`}</Box>}
            {dashboardUrl && (
              <TextLink href={dashboardUrl} target="_blank" rel="noreferrer">
                View in Recheck
              </TextLink>
            )}
          </Box>
        )}
      </Box>
      {pendingDecision && (
        <WelcomeCallReviewModal accountId={accountId} decision={pendingDecision} onClose={closeModal} />
      )}
    </>
  );
};

export default WelcomeCallQueueReview;
